/**
 * Cache utilities for Pinia stores
 */

import { getEntry } from '@/utils/indexedDBHelper'

/**
 * Check if cached data is valid
 * @param {Object} cacheInfo - Cache information object
 * @param {number} cacheDuration - Duration in minutes (0 means no caching)
 * @returns {boolean} - Whether cache is still valid
 */
export const isCacheValid = (cacheInfo, cacheDuration) => {
  // If cacheDuration is 0, no caching is desired
  if (cacheDuration === 0) return false

  // If no cache info or timestamp, cache is invalid
  if (!cacheInfo || !cacheInfo.timestamp) return false

  // Calculate if cache has expired
  const now = Date.now()
  const expiryTime = cacheInfo.timestamp + cacheDuration * 60 * 1000

  return now < expiryTime
}

/**
 * Create a cache info object
 * @returns {Object} - Cache info with current timestamp
 */
export const createCacheInfo = () => {
  return {
    timestamp: Date.now()
  }
}

/**
 * Generate a cache key by combining a base key with a version identifier
 * @param {string} baseKey - The base key for the cache entry
 * @param {string} version - The version identifier
 * @returns {string} - The combined cache key
 */
export const generateCacheKey = (baseKey, version) => {
  return `${version}_${baseKey}`
}

/**
 * Get data from local cache
 * @param {string} key - Cache key
 * @returns {any} - Cached data or null if not found
 */
export const getCached = (key) => {
  try {
    const cachedItem = localStorage.getItem(`cache_${key}`)
    return cachedItem ? JSON.parse(cachedItem) : null
  } catch (error) {
    console.error('Error retrieving cached data:', error)
    return null
  }
}

/**
 * Store data in local cache
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 */
export const cacheData = (key, data) => {
  try {
    localStorage.setItem(`cache_${key}`, JSON.stringify(data))
  } catch (error) {
    console.error('Error caching data:', error)
  }
}

/**
 * Get the timestamp from an IndexedDB cache entry.
 * Returns 0 when missing/unavailable.
 */
export const getIdbCacheTimestamp = async (dbName, storeName, cacheKey) => {
  try {
    const entry = await getEntry(dbName, storeName, cacheKey)
    const ts = entry && typeof entry.timestamp === 'number' ? entry.timestamp : 0
    return Number(ts) || 0
  } catch {
    return 0
  }
}

/**
 * Persisted cache timestamp helpers (centralized)
 * Use per-entity and per-version keys so any store can reuse this.
 * Key format: rulesCache.{entity}.{versionId}.ts
 */
const persistedTsKey = (entity, versionId) => `rulesCache.${entity}.${versionId}.ts`

export const getPersistedCacheTs = (entity, versionId) => {
  try {
    return Number(localStorage.getItem(persistedTsKey(entity, versionId))) || 0
  } catch {
    return 0
  }
}

export const setPersistedCacheTs = (entity, versionId, ts) => {
  try {
    localStorage.setItem(persistedTsKey(entity, versionId), String(ts))
  } catch { /* ignore */ }
}

export const clearPersistedCacheTs = (entity, versionId) => {
  try {
    localStorage.removeItem(persistedTsKey(entity, versionId))
  } catch { /* ignore */ }
}
